import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, Download, Loader2, FileText, ExternalLink, Trash2,
  CheckCircle2, AlertTriangle, Building2, User, Calendar, Hash, RefreshCw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { formatIndianDate } from "../utils/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(n || 0);

const REASON_LABEL = {
  sales_return: "Sales Return",
  quantity_reduction: "Quantity Reduction",
  rate_correction: "Rate Correction",
  wrong_gst_applied: "Wrong GST Applied",
  cancelled_order: "Cancelled Order",
  other: "Other",
};

const STATUS_BADGE = {
  draft: "bg-amber-100 text-amber-800 border-amber-200",
  issued: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function CreditNoteDetailPage() {
  const { creditNoteId } = useParams();
  const navigate = useNavigate();
  const [cn, setCn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchCN(); }, [creditNoteId]);

  const fetchCN = async () => {
    try {
      const res = await axios.get(`${API}/builder/credit-notes/${creditNoteId}`, { withCredentials: true });
      setCn(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Credit Note");
      navigate("/credit-notes");
    } finally { setLoading(false); }
  };

  const handleIssue = async () => {
    if (!window.confirm("Issue this Credit Note?\n\nOnce issued it becomes locked from edits and can be downloaded as a PDF.")) return;
    setIssuing(true);
    try {
      await axios.post(`${API}/builder/credit-notes/${creditNoteId}/issue`, {}, { withCredentials: true });
      toast.success("Credit Note issued");
      fetchCN();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to issue");
    } finally { setIssuing(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/builder/credit-notes/${creditNoteId}/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cn.credit_note_number || creditNoteId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    } finally { setDownloading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this draft Credit Note? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/builder/credit-notes/${creditNoteId}`, { withCredentials: true });
      toast.success("Draft deleted");
      navigate("/credit-notes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!cn) return null;

  const statusClass = STATUS_BADGE[cn.status] || STATUS_BADGE.draft;
  const isDraft = cn.status === "draft";

  return (
    <div className="min-h-screen bg-slate-50 py-6 sm:py-8 px-4" data-testid="credit-note-detail-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/credit-notes" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Credit Notes
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6 mb-5"
          data-testid="cn-detail-header"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
            <div className="flex gap-3 items-start">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="cn-number">
                    {cn.credit_note_number}
                  </h1>
                  <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border capitalize ${statusClass}`} data-testid="cn-status-badge">
                    {cn.status}
                  </span>
                </div>
                <p className="text-sm text-slate-500">
                  Issued <span className="font-medium text-slate-700">{formatIndianDate(cn.issue_date)}</span> · {cn.mode === "full" ? "Full Reversal" : "Partial Reversal"} · Reason: <span className="font-medium text-slate-700">{REASON_LABEL[cn.reason] || cn.reason}</span>
                </p>
                {cn.original_invoice_id && (
                  <Link
                    to={`/invoices/${cn.original_invoice_id}`}
                    className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 hover:underline"
                    data-testid="cn-original-link"
                  >
                    Against Invoice {cn.original_invoice_number} <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleDownload} disabled={downloading} variant="outline" className="text-sm" data-testid="cn-download-btn">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />} Download PDF
              </Button>
              {isDraft && (
                <>
                  <Button onClick={handleIssue} disabled={issuing} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm" data-testid="cn-issue-btn">
                    {issuing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Issue Credit Note
                  </Button>
                  <Button onClick={handleDelete} disabled={deleting} variant="outline" className="text-sm border-red-200 text-red-600 hover:bg-red-50" data-testid="cn-delete-btn">
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Delete Draft
                  </Button>
                </>
              )}
            </div>
          </div>

          {isDraft && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-sm" data-testid="cn-draft-banner">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800">
                This is a <strong>draft</strong>. Review the line items below, then click <strong>Issue Credit Note</strong> to lock it. The original invoice stays intact.
              </p>
            </div>
          )}
        </motion.div>

        {/* Customer + Original info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="cn-customer-card">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-slate-500" />
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</h3>
            </div>
            <p className="text-sm font-semibold text-slate-900">{cn.customer_name || "—"}</p>
            {cn.customer_gst && <p className="text-xs text-slate-500 font-mono mt-0.5">{cn.customer_gst}</p>}
            {cn.customer_address && <p className="text-xs text-slate-500 mt-1 whitespace-pre-line">{cn.customer_address}</p>}
            {cn.customer_email && <p className="text-xs text-slate-500 mt-1">{cn.customer_email}</p>}
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="cn-meta-card">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-slate-500" />
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Reference</h3>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <Hash className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-slate-500 text-xs">Original Invoice</span>
                <span className="font-medium ml-auto">{cn.original_invoice_number || "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-slate-500 text-xs">Original Date</span>
                <span className="font-medium ml-auto">{formatIndianDate(cn.original_invoice_date) || "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-slate-500 text-xs">Mode</span>
                <span className="font-medium ml-auto capitalize">{cn.mode}</span>
              </div>
            </div>
            {cn.reason_note && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500 mb-1">Note</p>
                <p className="text-xs text-slate-700">{cn.reason_note}</p>
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-5" data-testid="cn-items-card">
          <div className="p-5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Reversed Items</h3>
            <p className="text-xs text-slate-500 mt-0.5">Amounts to be credited back to the customer.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Item</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Qty</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Rate</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">GST</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody>
                {(cn.items || []).map((it, idx) => (
                  <tr key={idx} className="border-b border-slate-50 last:border-0" data-testid={`cn-item-row-${idx}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{it.name || "—"}</p>
                      {it.hsn_code && <p className="text-xs text-slate-500 font-mono mt-0.5">HSN {it.hsn_code}</p>}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{it.quantity}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{fmt(it.unit_price)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{it.gst_percent}%</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmt(it.total_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="bg-slate-50 border-t border-slate-100 p-5">
            <div className="ml-auto max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span><span>{fmt(cn.subtotal)}</span>
              </div>
              {cn.tax_amount > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>GST</span><span>{fmt(cn.tax_amount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-200 text-base font-bold text-slate-900" data-testid="cn-grand-total">
                <span>Credit Total</span><span>{fmt(cn.grand_total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
